import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Eye, Users, Clock, TrendingUp, RefreshCw } from "lucide-react";

function KPI({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-[#E5E7EB] p-6 hover:border-[#D4AF37] transition-colors">
      <Icon className="w-6 h-6 text-[#D4AF37]" />
      <div className="mt-4 font-display text-4xl tabular-nums">{Number(value).toLocaleString("en-IN")}</div>
      <div className="mt-1 tracking-eyebrow text-[#4B5563]">{label}</div>
      {sub && <div className="text-xs text-[#6B7280] mt-2">{sub}</div>}
    </div>
  );
}

export default function AnalyticsPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/analytics", { headers: { "X-Admin-Token": token } });
      setData(r.data);
      setUpdatedAt(new Date());
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  if (!data) {
    return (
      <div className="bg-white border border-[#E5E7EB] p-10 text-center">
        <p className="text-[#6B7280] text-sm">{loading ? "Loading analytics…" : "No analytics yet."}</p>
      </div>
    );
  }

  const maxDaily = Math.max(1, ...data.daily_last_7.map((d) => d.count));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl">Site Analytics</h2>
          <p className="text-xs text-[#6B7280] mt-1">
            Auto-refreshes every minute · Last updated {updatedAt ? updatedAt.toLocaleTimeString() : "—"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-widest font-semibold flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label>
          <button onClick={load} disabled={loading} data-testid="analytics-refresh"
            className="text-xs uppercase tracking-widest font-semibold px-4 py-2 border border-[#E5E7EB] hover:border-[#1A1A1A] inline-flex items-center gap-2 disabled:opacity-60">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh now
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Eye} label="Total Visits" value={data.total_visits} data-testid="kpi-total" />
        <KPI icon={Users} label="Unique Visitors" value={data.unique_visitors} />
        <KPI icon={Clock} label="Last Hour" value={data.last_hour} sub="Visits in the past 60 min" />
        <KPI icon={TrendingUp} label="Today" value={data.today} sub="Since 00:00 UTC" />
        <KPI icon={Users} label="Last 24h Unique" value={data.last_24h_unique} sub={`${data.last_24h} total`} />
        <KPI icon={Users} label="Last 7 days Unique" value={data.last_7d_unique} sub={`${data.last_7d} total`} />
        <KPI icon={Eye} label="Last 30 days" value={data.last_30d} sub="Total visits" />
        <KPI icon={Eye} label="All-time" value={data.total_visits} sub={`${data.unique_visitors} unique`} />
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Daily bar chart */}
        <div className="lg:col-span-3 bg-white border border-[#E5E7EB] p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-xl">Last 7 days</h3>
            <span className="tracking-eyebrow text-[#D4AF37]">Visits</span>
          </div>
          <div className="grid grid-cols-7 gap-3 h-56 items-end">
            {data.daily_last_7.map((d) => (
              <div key={d.date} className="flex flex-col items-center gap-2">
                <div className="text-xs font-semibold tabular-nums">{d.count}</div>
                <div className="w-full bg-[#FAFAFA] border border-[#E5E7EB] flex items-end h-full">
                  <div className="w-full bg-[#D4AF37]" style={{ height: `${(d.count / maxDaily) * 100}%` }}></div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">{d.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Top pages */}
        <div className="lg:col-span-2 bg-white border border-[#E5E7EB] p-6">
          <h3 className="font-display text-xl mb-4">Top pages (7d)</h3>
          {data.top_pages.length === 0 ? (
            <p className="text-sm text-[#6B7280]">No data yet.</p>
          ) : (
            <ul className="divide-y divide-[#E5E7EB]">
              {data.top_pages.map((p) => (
                <li key={p.page} className="flex items-center justify-between py-3">
                  <code className="text-xs bg-[#FAFAFA] px-2 py-1 border border-[#E5E7EB] truncate max-w-[200px]">{p.page}</code>
                  <span className="font-display text-lg tabular-nums">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-white border border-[#E5E7EB] p-6">
        <h3 className="font-display text-xl mb-4">Recent visits (20)</h3>
        {data.recent_visits.length === 0 ? (
          <p className="text-sm text-[#6B7280]">No visits recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#6B7280] border-b border-[#E5E7EB]">
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Page</th>
                  <th className="py-2 pr-4">Session</th>
                  <th className="py-2 pr-4">Referrer</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_visits.map((v) => (
                  <tr key={v.id} className="border-b border-[#F3F4F6]">
                    <td className="py-2 pr-4 text-[#4B5563] whitespace-nowrap">{new Date(v.created_at).toLocaleString()}</td>
                    <td className="py-2 pr-4"><code className="text-xs bg-[#FAFAFA] px-1.5 py-0.5">{v.page}</code></td>
                    <td className="py-2 pr-4 text-xs font-mono text-[#6B7280]">{(v.session_id || "").slice(0, 8)}…</td>
                    <td className="py-2 pr-4 text-xs text-[#6B7280] truncate max-w-[200px]">{v.referrer || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
